/* eslint-disable react/button-has-type */
/* eslint-disable react/no-danger */
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Transformer } from 'markmap-lib';
import type { IPureNode } from 'markmap-common';
import type {} from 'markmap-lib';
import { useParams } from 'react-router-dom';
import { Markmap } from 'markmap-view';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { IAlertProps } from '../../interface/IAlertProps';
import { getDefaultAlertProps } from '../../utils/utils';
import { API_STATUS_PROBLEM } from '../../constant/constants';
import { AlertSeverityEnum } from '../../enum/AlertSeverityEnum';
import { MyAlert } from '../UI/MyAlert';
import { ContentDto } from '../../dto/ContentDto';
import { BackDropDiv } from '../backdrop/BackDropDiv';
import { IApiResult } from '../../interface/IApiResult';
import { apiGetMindMapNew } from '../../service/apiGetMindMapNew';
import { MindMapDto } from '../../dto/MindMapDto';
import { apiSaveMindMapNew } from '../../service/apiSaveMindMapNew';
import styles from './Dialog.module.css';
import { enhanceMarkmapRoot } from '../../utils/markmap-transform-utils';
import { MindmapViewer } from './MindmapViewer';

interface Props {
  open: boolean;
  isEdit?: boolean;
  onClose: () => void;
  dto?: ContentDto;
}

export const DialogMindmapNew: FunctionComponent<Props> = ({
  open,
  dto,
  isEdit,
  onClose,
}) => {
  const [alertProps, setAlertProps] = useState<IAlertProps>(
    getDefaultAlertProps()
  );
  const [backDrop, setBackDrop] = useState(false);
  const { section } = useParams();
  const [mindmap, setMindmap] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [prosmotr, setProsmotr] = useState(false);
  const [viewerKey, setViewerKey] = useState(0);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentRootJson, setCurrentRootJson] = useState<IPureNode | null>(
    null
  );

  const handleClose = () => {
    setMindmap('');
    setProsmotr(false);
    setCurrentRootJson(null);
    setViewerOpen(false);
    onClose();
  };
  const svgRef = useRef<SVGSVGElement>(null);
  const markmapRef = useRef<Markmap | null>(null);

  const transformer = new Transformer();

  const convertToJSON = () => {
    let json = '';
    try {
      // const { root } = transformer.transform(mindmap);
      // json = JSON.stringify(root, null, 2);
      const result = transformer.transform(mindmap);
      const enhancedRoot = enhanceMarkmapRoot(result.root, mindmap);

      // Нормализуем структуру, заменяя undefined на []
      const normalizedRoot = {
        ...enhancedRoot,
        children: enhancedRoot.children || [],
      } as IPureNode;

      result.root = normalizedRoot;
      json = JSON.stringify(result.root, null, 2);
      setCurrentRootJson(result.root);
      // console.log(json);
    } catch (error) {
      json = '';
    }
    if (json === '') {
      if (svgRef.current) {
        while (svgRef.current.firstChild) {
          svgRef.current.removeChild(svgRef.current.firstChild);
        }
      }
    }
    setJsonOutput(json);
  };

  const [type, setType] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    if (markmapRef.current) {
      markmapRef.current = null;
    }
    // Очищаем SVG контейнер
    if (svgRef.current) {
      while (svgRef.current.firstChild) {
        svgRef.current.removeChild(svgRef.current.firstChild);
      }
    }
    setType(newValue);
  };

  const getMindMap = async (route: string, key: string) => {
    setBackDrop(true);
    const apiResult: IApiResult = await apiGetMindMapNew(route, key, `${type}`);
    if (apiResult.isSuccess) {
      setMindmap(apiResult.data);
    } else if (apiResult.status !== 404) {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
    setBackDrop(false);
  };

  const saveText = async (route: string, key: string) => {
    const mindMap: MindMapDto = {
      text: mindmap,
      json: jsonOutput,
    };
    const apiResult: IApiResult = await apiSaveMindMapNew(
      route,
      key,
      `${type}`,
      mindMap
    );
    if (apiResult.isSuccess) {
      setAlertProps({
        message: 'Данные записаны',
        severity: AlertSeverityEnum.info,
      });
      getMindMap(section ?? '', dto?.key ?? '');
    } else {
      setAlertProps({
        message: API_STATUS_PROBLEM,
        severity: AlertSeverityEnum.error,
      });
    }
  };

  const prosmotrKarti = () => {
    setProsmotr(!prosmotr);
    setViewerKey((prev) => prev + 1);
    setViewerOpen(true);
  };

  useEffect(() => {
    if (open) {
      getMindMap(section ?? '', dto?.key ?? '');
    }
    return () => {
      markmapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, type]);

  useEffect(() => {
    convertToJSON();

    if (!svgRef.current || !mindmap) return;

    // Создаем экземпляр Markmap если его нет
    if (!markmapRef.current) {
      markmapRef.current = Markmap.create(svgRef.current, {
        maxWidth: 300,
      });
    }

    try {
      const { root } = transformer.transform(mindmap);
      markmapRef.current.setData(root);
      markmapRef.current.fit();
    } catch (error) {
      console.error('Ошибка рендеринга:', error);
    }
  }, [mindmap]);

  const handleMarkdownChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newMarkdown = e.target.value;
    setMindmap(newMarkdown);
  };

  return (
    <div>
      <Dialog
        open={open}
        fullWidth
        maxWidth="xl"
        style={{ userSelect: 'none' }}
        PaperProps={{
          sx: {
            backgroundColor: '#e7ebee',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
          },
        }}
      >
        <BackDropDiv open={backDrop} />
        <MyAlert
          message={alertProps.message}
          severity={alertProps.severity}
          onClose={() => setAlertProps({ ...alertProps, message: '' })}
        />
        <DialogTitle>
          <Stack direction="column">
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography
                style={{
                  fontFamily: 'sans-serif',
                  fontStyle: 'normal',
                  fontWeight: 500,
                  fontSize: '24px',
                  color: '#000000',
                  letterSpacing: 1,
                }}
                component="text"
              >
                {`Карта - ${dto?.key} ${dto?.title}`}
              </Typography>
              <Stack direction="row" spacing={1}>
                <IconButton title="Закрыть" size="small" onClick={handleClose}>
                  <CloseIcon fontSize="large" sx={{ color: '#000000' }} />
                </IconButton>
              </Stack>
            </Stack>
            <header className={styles.editorHeader}>
              <div className={styles.toolbar}>
                <button
                  className={styles.toolbarButton}
                  onClick={() => saveText(section ?? '', dto?.key ?? '')}
                  title="Сохранить"
                >
                  💾
                </button>
                <button
                  className={styles.toolbarButton}
                  onClick={() => prosmotrKarti()}
                  title="Просмотр карты"
                >
                  Просмотр карты
                </button>
              </div>
            </header>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={type}
                onChange={handleChange}
                aria-label="basic tabs example"
              >
                <Tab label="Текущий" value={0} />
                <Tab label="Привес" value={1} />
                <Tab label="Сапин" value={2} />
                <Tab label="Гайворонский" value={3} />
              </Tabs>
            </Box>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="row" sx={{ height: '100%', minHeight: '500px' }}>
            {prosmotr !== true && (
              <textarea
                id="markdown-textarea"
                value={mindmap}
                onChange={handleMarkdownChange}
                placeholder="Начните писать mindmap здесь..."
                style={{
                  flex: 1,
                  width: '50%',
                  padding: '16px',
                  fontFamily: 'monospace',
                  fontSize: '14px',
                  resize: 'none',
                  border: 'none',
                  borderRight: '1px solid #e0e0e0',
                  outline: 'none',
                  backgroundColor: '#fafafa',
                }}
              />
            )}
            <div
              style={{
                flex: 1,
                width: '50%',
                marginLeft: '16px',
                backgroundColor: '#1b1d21',
                borderRadius: '4px',
                border: '1px solid #ccc',
                overflow: 'hidden',
              }}
            >
              {currentRootJson &&
                currentRootJson.content !== '' &&
                jsonOutput !== '' && (
                  <MindmapViewer
                    isOpen={open}
                    rootJson={currentRootJson}
                    title=""
                    key={`${dto?.key}-${Date.now()}`}
                  />
                )}
            </div>
          </Stack>
        </DialogContent>
      </Dialog>
    </div>
  );
};

DialogMindmapNew.defaultProps = {
  isEdit: false,
  dto: undefined,
};
